import { ActivatedPack } from '../services/authService';
import { ProcessedTicketData } from '../types/processedTicket';
import {
  buildTicketBarcodeValue,
  buildTicketDisplayNumber,
  buildTicketNumberValue,
  parseStartingTicketNumber,
} from './ticketFormatters';

// Computes sum of digits modulo 10.
const calculateCheckDigit = (input: string) => {
  let sum = 0;
  for (let i = 0; i < input.length; i += 1) {
    const num = parseInt(input[i], 10);
    if (!Number.isNaN(num)) {
      sum += num;
    }
  }
  return sum % 10;
};

// The current physical ticket includes one remaining ticket. END is the boundary
// after the printed range, independent of the direction in which tickets sell.
export const getTicketNumberForSoldCount = (params: {
  firstTicketNumber: number;
  ticketsPerPack: number;
  ticketsSold: number;
  direction?: 'backwards' | 'forwards';
}): number => {
  const { firstTicketNumber, ticketsPerPack, direction } = params;
  const sold = Math.max(0, Math.min(params.ticketsSold, ticketsPerPack));
  if (sold === ticketsPerPack) return firstTicketNumber + ticketsPerPack;
  return direction === 'forwards'
    ? firstTicketNumber + ticketsPerPack - 1 - sold
    : firstTicketNumber + sold;
};

// Builds a processed pack with parsed fields from starting_ticket_number.
export const buildProcessedTicketData = (
  pack: ActivatedPack,
  options?: {
    format?: string;
    barcodeLength?: number | null;
    scannerFormats?: string[];
    direction?: 'backwards' | 'forwards';
    regionStartingTicketNumber?: number | null;
  }
): ProcessedTicketData => {
  const parsed = parseStartingTicketNumber(pack.starting_ticket_number ?? 0, {
    format: options?.format,
  });
  const parsedGameNumber = parsed.gameNumber;
  const parsedPackId = parsed.packId;
  const parsedRandomDigit = parsed.randomDigit;
  const baseTicketNumber = options?.regionStartingTicketNumber ?? 0;
  const component = parsed.initialTicketsSold;
  const lastTicketNumber = baseTicketNumber + pack.tickets_per_pack - 1;
  const parsedInitialTicketsSold = component === lastTicketNumber + 1
    ? pack.tickets_per_pack
    : Math.max(0, Math.min(pack.tickets_per_pack, options?.direction === 'forwards'
      ? lastTicketNumber - component
      : component - baseTicketNumber));
  const checkDigit = calculateCheckDigit(parsedGameNumber + parsedPackId);

  const packType: 'lotto' | 'scratchers' = pack.pack_type ? pack.pack_type : 'scratchers';

  return {
    ...pack,
    tickets_sold: pack.tickets_sold ?? parsedInitialTicketsSold,
    pack_type: packType,
    parsedGameNumber,
    parsedPackId,
    parsedRandomDigit,
    parsedInitialTicketsSold,
    baseTicketNumber,
    checkDigit,
    barcode_format: options?.format,
    barcode_length: options?.barcodeLength,
    barcode_scanner_formats: options?.scannerFormats,
    direction: options?.direction,
  };
};

// Builds processed pack list with shared format metadata.
export const buildProcessedTicketDataList = (
  packs: ActivatedPack[],
  options?: {
    format?: string;
    barcodeLength?: number | null;
    scannerFormats?: string[];
    direction?: 'backwards' | 'forwards';
    regionStartingTicketNumber?: number | null;
  }
): ProcessedTicketData[] => {
  return packs
    .map((pack) => buildProcessedTicketData(pack, options))
    .filter((pack) => pack.parsedPackId !== '');
};

// Builds a barcode string from game/pack/ticket inputs.
export const buildTicketBarcode = (params: {
  format?: string;
  gameNumber: string;
  packId: string;
  ticketNumber: number;
}): string => {
  return buildTicketBarcodeValue(params);
};

// Builds a display string from game/pack/ticket inputs.
export const buildTicketDisplay = (params: {
  format?: string;
  gameNumber: string;
  packId: string;
  randomDigit: string;
  ticketNumber: number;
}): string => {
  return buildTicketDisplayNumber(params);
};

export const parseStartingTicket = parseStartingTicketNumber;

// Builds the starting ticket number using the format and pack identifiers.
export const buildStartingTicketNumber = (params: {
  format?: string;
  gameNumber: string;
  packId: string;
  ticketsSold?: number;
}): string => {
  return buildTicketNumberValue({
    format: params.format,
    gameNumber: params.gameNumber,
    packId: params.packId,
    ticketNumber: params.ticketsSold ?? 0,
  });
};

export const formatTicketData = (params: {
  format?: string;
  gameNumber?: string;
  packId?: string;
  randomDigit?: string;
  ticketNumber?: number;
}) => {
  return {
    displayNumber: buildTicketDisplayNumber({
      format: params.format,
      gameNumber: params.gameNumber ?? '',
      packId: params.packId ?? '',
      randomDigit: params.randomDigit ?? '',
      ticketNumber: params.ticketNumber ?? 0,
    }),
    barcodeValue: buildTicketBarcodeValue({
      format: params.format,
      gameNumber: params.gameNumber ?? '',
      packId: params.packId ?? '',
      ticketNumber: params.ticketNumber ?? 0,
    }),
  };
};
